"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { CtaButton } from "@/components/layout/CtaButton";
import { submitForm } from "@/lib/submitForm";

export type LeadFormProps = {
  id?: string;
  heading?: string;
  subheading?: string;
  submitLabel?: string;
  badgeImageSrc?: string;
  badgeAlt?: string;
  /** Intro panel form: green fields, email/phone row, privacy checkbox */
  variant?: "default" | "intro";
  onSubmit?: (data: LeadFormData) => void;
  className?: string;
};

export type LeadFormData = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
};

export function LeadForm({
  id = "lead",
  heading = "Expert Publishing Blueprint",
  subheading,
  submitLabel = "Get My Free Publishing Guide",
  badgeImageSrc,
  badgeAlt = "Free guide included badge",
  variant = "default",
  onSubmit,
  className,
}: LeadFormProps) {
  const router = useRouter();
  const [formData, setFormData] = useState<LeadFormData>({
    fullName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const isIntro = variant === "intro";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("idle");

    try {
      const result = await submitForm({
        source: id,
        fields: formData,
        honeypot,
      });

      if (!result.ok) {
        setStatus("error");
        setStatusMessage(result.error);
        return;
      }

      if (onSubmit) {
        await onSubmit(formData);
      }

      const ppcData =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem("ubp:ppc")
          : null;

      if (
        typeof window !== "undefined" &&
        Array.isArray(
          (window as unknown as { dataLayer?: unknown[] }).dataLayer
        )
      ) {
        (window as unknown as { dataLayer: unknown[] }).dataLayer.push({
          event: "lead_form_submit",
          ...formData,
          ppc: ppcData ? JSON.parse(ppcData) : null,
        });
      }

      setFormData({ fullName: "", email: "", phone: "", message: "" });
      setPrivacyAccepted(false);
      router.push("/thank-you/");
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus("error");
      setStatusMessage("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldId = (name: string) => `${id}-${name}`;

  const rootClass = [
    "form-wrap text-left",
    isIntro
      ? "relative overflow-visible p-0"
      : "form-wrapper relative overflow-visible shadow-xl",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div id={id} className={rootClass}>
      {badgeImageSrc && (
        <div className="absolute -right-3 -top-3 h-16 w-16 sm:-right-5 sm:-top-5 sm:h-20 sm:w-20">
          <Image
            src={badgeImageSrc}
            alt={badgeAlt}
            width={80}
            height={80}
            unoptimized
            sizes="80px"
            quality={50}
            loading="lazy"
            fetchPriority="low"
            className="h-full w-full object-contain"
          />
        </div>
      )}

      {heading ? (
        <div className={`form-heading${isIntro ? "" : " mb-4"}`}>
          {isIntro ? (
            <h3 className="font-heading font-50 uppercase text-white">
              START YOUR <br /> BOOK THE SMART WAY
            </h3>
          ) : (
            <h2 className="font-heading font-40 uppercase text-white">
              {heading}
            </h2>
          )}
          {subheading && (
            <p className="mt-2 text-white">{subheading}</p>
          )}
        </div>
      ) : null}

      <form
        onSubmit={handleSubmit}
        className={isIntro ? "text-left" : "bann-form space-y-0 text-left"}
      >
        {/* Honeypot — hidden from real users, bots that autofill every field will trip it */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden"
        />

        <div className={isIntro ? undefined : "field-box"}>
          <label htmlFor={fieldId("fullName")}>
            Enter Your Full Name
          </label>
          <input
            type="text"
            id={fieldId("fullName")}
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Mark Hunt"
            required
            className="form-control"
          />
        </div>

        {isIntro ? (
          <div className="grid gap-x-4 sm:grid-cols-2">
            <div>
              <label htmlFor={fieldId("email")}>Your email</label>
              <input
                type="email"
                id={fieldId("email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="info@example.com"
                required
                className="form-control"
              />
            </div>
            <div>
              <label htmlFor={fieldId("phone")}>Your phone</label>
              <input
                type="tel"
                id={fieldId("phone")}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(999) 999 9999"
                required
                className="form-control"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="field-box">
              <label htmlFor={fieldId("email")}>Your email</label>
              <input
                type="email"
                id={fieldId("email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="info@example.com"
                required
                className="form-control"
              />
            </div>
            <div className="field-box">
              <label htmlFor={fieldId("phone")}>Your phone</label>
              <input
                type="tel"
                id={fieldId("phone")}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(999) 999 9999"
                required
                className="form-control"
              />
            </div>
          </>
        )}

        <div className={isIntro ? undefined : "field-box"}>
          <label htmlFor={fieldId("message")}>Type your message</label>
          <textarea
            id={fieldId("message")}
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Your Message"
            rows={4}
            className="form-control resize-y"
          />
        </div>

        {isIntro && (
          <div className="mt-3 flex items-start gap-2">
            <input
              id={fieldId("privacy")}
              type="checkbox"
              required
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border border-white/40 bg-transparent accent-[#b46c29]"
            />
            <label
              htmlFor={fieldId("privacy")}
              className="form-check-label cursor-pointer text-white"
            >
              By submitting this form, you confirm the details you&apos;ve
              shared are accurate. For full information on data use, please
              review our{" "}
              <Link
                href="/privacy-policy"
                className="font-medium text-[#f1d0ae] underline underline-offset-2"
                target="_blank"
              >
                Privacy Policy
              </Link>
              .
            </label>
          </div>
        )}

        <div className={isIntro ? "btn-container mt-4" : "btn-container mt-4"}>
          <CtaButton
            type="submit"
            variant="primary"
            size="md"
            className={
              isIntro
                ? "s1-form-submit w-full border-0 disabled:cursor-not-allowed disabled:opacity-50"
                : "s1-form-submit w-full disabled:cursor-not-allowed disabled:opacity-50"
            }
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : submitLabel}
          </CtaButton>
        </div>

        {status !== "idle" && (
          <p
            role="status"
            className={
              status === "success"
                ? "mt-3 text-sm text-white"
                : "mt-3 text-sm text-red-300"
            }
          >
            {statusMessage}
          </p>
        )}
      </form>
    </div>
  );
}
